import React, { useMemo, useEffect, useState } from 'react';
import { Batiment, Personnel, Vehicule, Intervention } from '../../../../shared/types/game';
import { getPlanningServiceKeyFromBuildingType } from '../../utils/buildingServiceMapping';
import { DEFAULT_PLANNING_NOM, getPlanningLabelForLocalDate } from '../OperationalPlanningModal';
import {
  sortPersonnelInGroup,
  comparePlanningGroupsByPrio,
  resolvePlanningRowForLabel,
  planningBadgeCode,
  isPlanningPersonnelsJour,
} from './buildingDetailsTabHelpers';
import { getApiBaseUrl } from '../../../../core/config/api';

interface BuildingGererTabProps {
  building: Batiment;
  personnel: Personnel[];
  vehicules: Vehicule[];
  loadingPersonnel: boolean;
  loadingVehicules: boolean;
  onPersonnelAdded?: () => void;
  onVehiculeAdded?: () => void;
}

const vehiculeStatusColor = (v: Vehicule): 'green' | 'orange' | 'gray' | 'red' => {
  if (v.statut === 'disponible') return 'green';
  if (v.statut === 'en_mission') return 'orange';
  if (v.statut === 'panne') return 'red';
  return 'gray';
};

const chipClass: Record<'green' | 'orange' | 'gray' | 'red', string> = {
  green:  'bg-emerald-600/90 text-white ring-1 ring-emerald-500/30',
  orange: 'bg-amber-600/90   text-white ring-1 ring-amber-500/30',
  gray:   'bg-zinc-600/80    text-zinc-200 ring-1 ring-zinc-500/20',
  red:    'bg-red-700/80     text-white ring-1 ring-red-500/30',
};

const SectionHeader: React.FC<{ icon: string; title: string; sub?: string }> = ({ icon, title, sub }) => (
  <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-zinc-800">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-zinc-700/60 bg-zinc-800/80 text-base">
      {icon}
    </span>
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-emerald-300/90">{title}</p>
      {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const BuildingGererTab: React.FC<BuildingGererTabProps> = ({
  building,
  personnel,
  vehicules,
  loadingPersonnel,
  loadingVehicules,
  onPersonnelAdded,
  onVehiculeAdded,
}) => {
  const [interventions, setInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await fetch(`${getApiBaseUrl(true)}/buildings/interventions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setInterventions(data.data);
      } catch { /* silently ignore */ }
    };
    load();
  }, [building.id]);

  const planningServiceKey = useMemo(
    () => getPlanningServiceKeyFromBuildingType(building.type),
    [building.type]
  );

  const personnelParPlanning = useMemo(() => {
    const today = new Date();
    const m = new Map<string, Personnel[]>();
    for (const p of personnel) {
      const key = String(getPlanningLabelForLocalDate(p, today) || DEFAULT_PLANNING_NOM).trim() || DEFAULT_PLANNING_NOM;
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(p);
    }
    return Array.from(m.entries())
      .map(([k, v]) => [k, sortPersonnelInGroup(v)] as const)
      .sort(([a], [b]) => comparePlanningGroupsByPrio(a, b, planningServiceKey));
  }, [personnel, planningServiceKey]);

  const { personnelsDisponibles, personnelsDispoDepart } = useMemo(() => {
    const today = new Date();
    const actifs = personnel.filter((p) => p.actif);
    return {
      personnelsDisponibles: actifs.length,
      personnelsDispoDepart: actifs.filter((p) => !isPlanningPersonnelsJour(p, today)).length,
    };
  }, [personnel]);

  const vehiculeIdsDuBatiment = useMemo(() => new Set(vehicules.map((v) => Number(v.id))), [vehicules]);

  const interventionsActives = useMemo(() => {
    const bid = Number(building.id);
    return interventions.filter((i) => {
      if (i.statut !== 'en_cours' && i.statut !== 'en_attente') return false;
      if (Number(i.batiment_id) === bid) return true;
      return i.vehicules_ids?.some((vid) => vehiculeIdsDuBatiment.has(Number(vid))) ?? false;
    });
  }, [interventions, building.id, vehiculeIdsDuBatiment]);

  const enginesEngages = useMemo(
    () => vehicules.filter((v) => v.statut === 'en_mission'),
    [vehicules]
  );

  const totalCapaPersonnel = (building.postes_disponibles ?? 0) + (building.postes_occupes ?? 0);
  const totalCapaVehicules = building.capacite_vehicules ?? 0;

  return (
    <div className="grid grid-cols-3 gap-4 h-full">

      {/* ── COLONNE 1 : PERSONNEL ── */}
      <div className="flex flex-col bg-zinc-900/60 rounded-xl border border-zinc-800/80 overflow-hidden">
        <div className="p-4 flex-1 overflow-y-auto">
          <SectionHeader
            icon="👥"
            title="Personnels"
            sub={`Total : ${personnel.length} · Disponibles : ${personnelsDisponibles} · Dispo départ : ${personnelsDispoDepart}`}
          />
          {loadingPersonnel ? (
            <p className="text-center text-sm text-zinc-500 py-8">Chargement…</p>
          ) : personnel.length === 0 ? (
            <p className="text-center text-sm text-zinc-500 py-8">Aucun personnel assigné.</p>
          ) : (
            <div className="space-y-3">
              {personnelParPlanning.map(([statutLabel, rows]) => {
                const planRow = resolvePlanningRowForLabel(statutLabel, planningServiceKey);
                const badgeStyle: React.CSSProperties = planRow
                  ? { backgroundColor: planRow.couleurs.backgroundColor, color: planRow.couleurs.textColor }
                  : { backgroundColor: '#3f3f46', color: '#fafafa' };
                return (
                  <article key={statutLabel} className="rounded-lg border border-zinc-700/80 overflow-hidden">
                    <div className="flex items-center justify-between gap-2 border-b border-zinc-800 bg-zinc-800/70 px-3 py-2">
                      <span className="text-[0.68rem] font-bold uppercase tracking-widest text-zinc-200">
                        {statutLabel}
                        <span className="ml-1.5 text-zinc-500">· {rows.length}</span>
                      </span>
                      <span
                        className="shrink-0 rounded-full border border-zinc-600/40 px-2 py-0.5 text-[0.62rem] font-bold tracking-wide"
                        style={badgeStyle}
                      >
                        {planningBadgeCode(planRow, statutLabel)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 p-3">
                      {rows.map((p) => (
                        <div
                          key={p.id}
                          className="rounded-lg px-3 py-2 shadow-md"
                          style={
                            planRow
                              ? { background: `linear-gradient(to bottom, ${planRow.couleurs.backgroundColor}, ${planRow.couleurs.backgroundColor}cc)` }
                              : { background: 'linear-gradient(to bottom, #059669, #065f46)' }
                          }
                        >
                          <div className="text-[0.72rem] font-bold uppercase tracking-wide text-white leading-tight">
                            {p.prenom} {p.nom}
                          </div>
                          <div className="text-[0.6rem] font-semibold uppercase tracking-wider text-white/75 mt-0.5">
                            {p.grade}
                          </div>
                        </div>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
        {onPersonnelAdded && (
          <div className="p-3 border-t border-zinc-800">
            <button
              onClick={onPersonnelAdded}
              className="w-full rounded-lg border border-dashed border-zinc-600 bg-transparent py-2 text-xs text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors"
            >
              + Ajouter du personnel
            </button>
          </div>
        )}
      </div>

      {/* ── COLONNE 2 : ENGINS ── */}
      <div className="flex flex-col bg-zinc-900/60 rounded-xl border border-zinc-800/80 overflow-hidden">
        <div className="p-4 flex-1 overflow-y-auto">
          <SectionHeader
            icon="🚒"
            title="Engins"
            sub={`${vehicules.length} engins · ${vehicules.filter((v) => v.statut === 'disponible').length} disponibles · ${enginesEngages.length} engagés`}
          />
          {loadingVehicules ? (
            <p className="text-center text-sm text-zinc-500 py-8">Chargement…</p>
          ) : vehicules.length === 0 ? (
            <p className="text-center text-sm text-zinc-500 py-8">Aucun engin assigné.</p>
          ) : (
            <>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-2">Statut en temps réel</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                {vehicules.map((v) => {
                  const color = vehiculeStatusColor(v);
                  return (
                    <span key={v.id} className={`rounded-lg px-2.5 py-1.5 text-[0.68rem] font-bold uppercase tracking-wide shadow-md ${chipClass[color]}`}>
                      {v.nom}
                    </span>
                  );
                })}
              </div>
              <div className="text-[10px] text-zinc-600">Vert = disponible · Orange = engagé · Gris = maintenance</div>
            </>
          )}
        </div>
        {onVehiculeAdded && (
          <div className="p-3 border-t border-zinc-800">
            <button
              onClick={onVehiculeAdded}
              className="w-full rounded-lg border border-dashed border-zinc-600 bg-transparent py-2 text-xs text-zinc-500 hover:border-zinc-400 hover:text-zinc-300 transition-colors"
            >
              + Ajouter un véhicule
            </button>
          </div>
        )}
      </div>

      {/* ── COLONNE 3 : MOYENS EN INTERVENTION ── */}
      <div className="flex flex-col gap-3 overflow-y-auto">

        {/* Capacités */}
        <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/80 p-4">
          <SectionHeader icon="📊" title="Capacités" />
          <div className="space-y-3">
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-zinc-400">Personnel</span>
                <span className="text-zinc-200 font-medium">{building.postes_occupes ?? 0} / {totalCapaPersonnel}</span>
              </div>
              <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-blue-500"
                  style={{ width: totalCapaPersonnel > 0 ? `${Math.round(((building.postes_occupes ?? 0) / totalCapaPersonnel) * 100)}%` : '0%' }}
                />
              </div>
            </div>
            {totalCapaVehicules > 0 && (
              <div>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="text-zinc-400">Véhicules</span>
                  <span className="text-zinc-200 font-medium">{vehicules.length} / {totalCapaVehicules}</span>
                </div>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500"
                    style={{ width: `${Math.round((vehicules.length / totalCapaVehicules) * 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Interventions actives */}
        <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/80 p-4">
          <SectionHeader
            icon="⚠️"
            title="Interventions"
            sub={`${interventionsActives.length} intervention${interventionsActives.length !== 1 ? 's' : ''} active${interventionsActives.length !== 1 ? 's' : ''}`}
          />
          <div className="flex items-center gap-1.5 mb-3">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-40" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400">Temps réel</span>
          </div>
          {interventionsActives.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-700 bg-zinc-950/30 py-5 text-center text-xs text-zinc-500">
              Aucun moyen en intervention
            </p>
          ) : (
            <div className="space-y-2">
              {interventionsActives.map((i) => (
                <div key={i.id} className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 bg-zinc-950/40 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-zinc-100 truncate">{i.type}</p>
                    {i.localisation && (
                      <p className="text-[10px] text-zinc-500 truncate mt-0.5">{i.localisation}</p>
                    )}
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    i.statut === 'en_cours' ? 'bg-sky-500/15 text-sky-300 border border-sky-500/30' : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                  }`}>
                    {i.statut === 'en_cours' ? 'En cours' : 'En attente'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Engins engagés */}
        <div className="bg-zinc-900/60 rounded-xl border border-zinc-800/80 p-4">
          <SectionHeader icon="🚨" title="Engins engagés" />
          {enginesEngages.length === 0 ? (
            <p className="text-xs text-zinc-500">Aucun engin engagé</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {enginesEngages.map((v) => (
                <span key={v.id} className={`rounded-lg px-2.5 py-1.5 text-[0.68rem] font-bold uppercase tracking-wide shadow-md ${chipClass.orange}`}>
                  {v.nom}
                </span>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default BuildingGererTab;
